<template>
  <form role="form">
    <payout-form-group :label="$t('Номер телефона')"
                       class="form-group__label_align_start"
                       :required="true"
                       :has-errors="model.errors.has('destinationAccount')">
      <filtered-input-wrapper v-model="model.values.destinationAccount"></filtered-input-wrapper>
      <payout-input-hint-block v-slot:hints :hint="$t('account_hint')"></payout-input-hint-block>
      <error-message-block v-slot:errors attribute="destinationAccount|*"
                           :errors="model.errors"></error-message-block>
    </payout-form-group>

    <payout-textfield-input-group :model='model'
                                  attribute='description'
                                  :label="$t('Назначение платежа')"></payout-textfield-input-group>

    <payout-input-amount-form-group
      :filter="amountFilter"
      :model="model"></payout-input-amount-form-group>

    <template v-if="isRurCurrency">
      <PayoutHintPanelFormGroup>
        {{$t('amount_hint')}}
      </PayoutHintPanelFormGroup>
    </template>

    <payout-estimated-destination-amount-form-group
      :remove-kopecks="true"
      :model="model"></payout-estimated-destination-amount-form-group>

    <payout-destination-currency-conversion-notice-form-group
      style="margin-top: 1em;"
      :model="model"></payout-destination-currency-conversion-notice-form-group>

    <payout-evaluate-fee-form-group :model="model"></payout-evaluate-fee-form-group>

    <template v-if="hasLimits()">
      <payout-toggable-hint-panel-form-group
        :heading="$t('Лимиты')"
        :link-text="$t('Информация о лимитах')">
        <template v-if="model.limitMinAmount && model.limitMinCurrency">
          <i18n tag="div" path="limit_min_text">
            <template #amount>{{ model.limitMinAmount }}</template>
            <template #currency>{{ model.limitMinCurrency }}</template>
          </i18n>
        </template>
        <template v-if="model.limitMaxAmount && model.limitMaxCurrency">
          <i18n tag="div" path="limit_max_text">
            <template #amount>{{ model.limitMaxAmount }}</template>
            <template #currency>{{ model.limitMaxCurrency }}</template>
          </i18n>
        </template>
        <div>
          {{ $t('text_currency') }}
        </div>
        <template v-if="model.destCurrency">
          <br/>
          <i18n tag="div" path="text_dest_currency">
            <template #currency>{{ model.destCurrency }}</template>
          </i18n>
        </template>

      </payout-toggable-hint-panel-form-group>
    </template>


    <payout-form-notification-block :model="model"></payout-form-notification-block>

    <hr/>
    <payout-group-verification-type :model="model"></payout-group-verification-type>
  </form>
</template>

<script lang="ts">
import AbstractPayoutForm from "@/components/forms/payout/abstract/AbstractPayoutForm.vue";
import {Component} from "vue-property-decorator";
import PayoutFormGroup from "@/components/forms/payout/PayoutFormGroup.vue";
import PayoutInputHintBlock from "@/components/forms/payout/PayoutInputHintBlock.vue";
import ErrorMessageBlock from "@/components/forms/payout/ErrorMessageBlock.vue";
import PayoutTextfieldInputGroup from "@/components/forms/payout/PayoutTextfieldInputGroup.vue";
import PayoutInputAmountFormGroup from "@/components/forms/payout/PayoutInputAmountFormGroup.vue";
import PayoutEstimatedDestinationAmountFormGroup
  from "@/components/forms/payout/forms/PayoutEstimatedDestinationAmountFormGroup.vue";
import PayoutEvaluateFeeFormGroup from "@/components/forms/payout/PayoutEvaluateFeeFormGroup.vue";
import PayoutToggableHintPanelFormGroup
  from "@/components/forms/payout/hint/PayoutToggableHintPanelFormGroup.vue";
import PayoutFormNotificationBlock
  from "@/components/forms/payout/forms/PayoutFormNotificationBlock.vue";
import PayoutGroupVerificationType
  from "@/components/payout/verification/PayoutGroupVerificationType.vue";
import FilteredInputWrapper from "@/components/forms/payout/forms/inputs/FilteredInputWrapper.vue";
import PayoutHintPanel from "@/components/forms/payout/PayoutHintPanel.vue";
import DropdownKeyvalue from "@/components/forms/dropdown/DropdownKeyValue.vue";
import DynamicFeeValueText from "@/components/forms/payout/forms/crypto/DynamicFeeValueText.vue";
import {MobilePaymentModel} from "./mobile-payment";
import PayoutDestinationCurrencyConversionNoticeFormGroup
  from "@/components/forms/payout/crypto/PayoutDestinationCurrencyConversionNoticeFormGroup.vue";
import {CurrencyCode} from "@/models/core/currency";
import PayoutHintPanelFormGroup from "@/components/forms/payout/hint/PayoutHintPanelFormGroup.vue";

@Component({
  name: "MobilePaymentForm",
  components: {
    PayoutHintPanelFormGroup,
    PayoutDestinationCurrencyConversionNoticeFormGroup,
    DynamicFeeValueText,
    DropdownKeyvalue,
    PayoutHintPanel,
    FilteredInputWrapper,
    PayoutGroupVerificationType,
    PayoutFormNotificationBlock,
    PayoutToggableHintPanelFormGroup,
    PayoutEvaluateFeeFormGroup,
    PayoutEstimatedDestinationAmountFormGroup,
    PayoutInputAmountFormGroup,
    PayoutTextfieldInputGroup, ErrorMessageBlock, PayoutInputHintBlock, PayoutFormGroup
  }
})
export default class MobilePaymentForm extends AbstractPayoutForm {
  declare model: MobilePaymentModel

  hasLimits() {
    return this.model?.limitMinAmount || this.model?.limitMaxAmount
  }

  get isRurCurrency() {
    return this.model?.getSourceAccount() && (this.model?.getSourceAccount().currency === CurrencyCode.RUR)
  }

  amountFilter(value: string) {
    const currency = this.model?.getSourceAccount()?.currency;
    // console.log('amountFilter', value, currency)
    if (currency === CurrencyCode.RUR) {
      const res = String(value ? value : '').replace(/[\.\,][0-9]*$/g, '');
      // console.log('amountFilter res', res)
      return res
    }
    return value
  }
}
</script>

<style scoped>

</style>

<i18n>
{
  "en": {
    "account_hint": "Phone number.",
    "Назначение платежа": "Description",
    "Адрес получателя": "Beneficiary address",
    "Динамическая комиссия": "Dynamic commission",
    "Номер телефона": "Phone number",
    "Лимиты": "Limits",
    "Информация о лимитах": "Limits information",
    "limit_min_text": "Minimum amount: {amount} {currency}.",
    "limit_max_text": "Maximum amount: {amount} {currency} or equivalent in selected currency.",
    "text_currency": "Payment currency is Russian rubles.",
    "text_dest_currency": "In case of sending payments not from {currency} accounts of Capitalist, the amount will be automatically converted to {currency} at the current rate.",
    "amount_hint": "For payments in rubles, the amount must be without kopecks."
  },
  "ru": {
    "amount_hint": "Для платежей в рублях сумма должна быть без копеек.",
    "account_hint": "Номер телефона.",
    "limit_min_text": "Минимальный разовый платеж: {amount} {currency}.",
    "limit_max_text": "Максимальный разовый платеж: {amount} {currency} или эквивалент в выбранной валюте.",
    "text_currency": "Валюта исполнения платежей — рубли РФ.",
    "text_dest_currency": "В случае отправки платежей не с {currency} счетов Capitalist сумма будет автоматически сконвертирована в {currency} по текущему курсу."
  }
}
</i18n>
